type TemplateWithDefaults = {
  doc_templates_tenants?:
    | readonly ({
        tenant_id?: string | null;
        is_default?: boolean | null;
      } | null)[]
    | null;
  doc_templates_teams?:
    | readonly ({
        team_id?: string | null;
        is_default?: boolean | null;
      } | null)[]
    | null;
};

type TemplateDefaultScope = {
  tenantId: string | null | undefined;
  teamId: string | null | undefined;
};

const isTeamDefaultTemplate = (
  template: TemplateWithDefaults | null | undefined,
  teamId: string | null | undefined,
): boolean =>
  teamId != null &&
  (template?.doc_templates_teams?.some(
    team => team?.team_id === teamId && team?.is_default === true,
  ) ??
    false);

const isTenantDefaultTemplate = (
  template: TemplateWithDefaults | null | undefined,
  tenantId: string | null | undefined,
): boolean =>
  tenantId != null &&
  (template?.doc_templates_tenants?.some(
    tenant => tenant?.tenant_id === tenantId && tenant?.is_default === true,
  ) ??
    false);

export const getTemplateDefaultRank = (
  template: TemplateWithDefaults | null | undefined,
  { tenantId, teamId }: TemplateDefaultScope,
): number => {
  if (isTeamDefaultTemplate(template, teamId)) {
    return 0;
  }
  if (isTenantDefaultTemplate(template, tenantId)) {
    return 1;
  }
  return 2;
};

export const sortTemplatesByDefault = <T extends TemplateWithDefaults>(
  templates: readonly T[],
  scope: TemplateDefaultScope,
): T[] =>
  [...templates].sort(
    (a, b) =>
      getTemplateDefaultRank(a, scope) - getTemplateDefaultRank(b, scope),
  );

export const pickDefaultTemplate = <
  T extends TemplateWithDefaults & {
    active?: boolean | null;
    type?: string | null;
  },
>(
  templates: readonly T[] | null | undefined,
  { tenantId, teamId, type }: TemplateDefaultScope & { type?: string | null },
): T | null => {
  const candidates = (templates ?? []).filter(
    template => template.active && (!type || template.type === type),
  );

  return (
    candidates.find(template => isTeamDefaultTemplate(template, teamId)) ??
    candidates.find(template => isTenantDefaultTemplate(template, tenantId)) ??
    candidates[0] ??
    null
  );
};
